import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

function getQueryParam(name, search) {
  const params = new URLSearchParams(search);
  return params.get(name);
}

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    planId: '',
    subscription_start: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subscriptionAmount, setSubscriptionAmount] = useState(0);

  useEffect(() => {
    // Obtener plan_id desde los parámetros de la URL o usar el plan por defecto
    const DEFAULT_PLAN_ID = process.env.REACT_APP_DEFAULT_PLAN_ID || 'mensualidad_150';
    const planId = getQueryParam('plan_id', location.search) || DEFAULT_PLAN_ID;

    // Extraer el monto del plan_id (formato: mensualidad_XXX)
    const amountMatch = planId.match(/_(\d+)$/);
    if (!amountMatch) {
      setError('Error: Formato de plan_id inválido. Debe ser mensualidad_XXX');
      return;
    }
    const amount = parseInt(amountMatch[1]) * 1000; // Convertir a pesos
    setSubscriptionAmount(amount);

    // Obtener la fecha de inicio desde el parámetro 'start' o usar la fecha actual
    const startParam = getQueryParam('start', location.search);
    let startDate = new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }).split(',')[0];
    // Convert from MM/DD/YYYY to YYYY-MM-DD format
    if (startDate) {
      const [month, day, year] = startDate.split('/');
      startDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    if (startParam && /^\d{4}-\d{2}-\d{2}$/.test(startParam)) {
      startDate = startParam;
    }
    setFormData(prev => ({ ...prev, planId, subscription_start: startDate }));
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${API_URL}/api/subscription/create`, formData);
      setSuccess('Suscripción creada exitosamente');
      console.log('Respuesta del servidor:', response.data);
      navigate('/success-request', { state: { email: formData.email, paymentOrSubscription: 'suscripción' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la suscripción');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img 
              src="/logo.jpeg" 
              alt="FCO Coaching" 
              style={{ 
                maxWidth: '200px', 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }} 
            />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Crear Nueva Suscripción
          </Typography>

          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'text.primary',
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 2,
            mb: 3,
            textAlign: 'center'
          }}>
            <Typography variant="h5" component="div" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Monto de Suscripción
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              ${subscriptionAmount.toLocaleString('es-CL')} CLP
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nombre Completo"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Fecha de Inicio"
              name="subscription_start"
              type="date"
              value={formData.subscription_start}
              margin="normal"
              required
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
              }}
              helperText="*El cobro se realizará cada 30 días."
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Al crear la suscripción, recibirás un correo con los detalles de la suscripción y pago.
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Crear Suscripción'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default SubscriptionPage;

